/**
 * The arithmetic behind the Trends page.
 *
 * Everything is worked out in the browser from the list the Expenses page
 * already loads. There is no trends endpoint and no stored total: a chart
 * cannot disagree with the records, because it is the records, added up.
 *
 * Kept free of React and of the network so the rules can be tested directly
 * (test/trends.test.ts): which records count, how a merchant's spellings are
 * folded together, where a month ends, and what "ready to claim" meant on a
 * past date.
 */
import type { CoveredPerson, Expense, HsaBalance, Reimbursement } from './types.js';

// ---------------------------------------------------------------------------
// Months
// ---------------------------------------------------------------------------

export const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** One number per calendar month, so a run of months is a run of integers. */
export const monthIndex = (year: number, month: number): number => year * 12 + (month - 1);
export const yearOf = (index: number): number => Math.floor(index / 12);

/** From `2026-08-14` or `2026-08`. Read from the text, never through `Date`: a day must not move with the timezone. */
export function monthOf(iso: string | null | undefined): number | null {
  const m = /^(\d{4})-(\d{2})/.exec(iso ?? '');
  return m ? monthIndex(Number(m[1]), Number(m[2])) : null;
}

export const monthKey = (index: number): string => `${yearOf(index)}-${String((index % 12) + 1).padStart(2, '0')}`;
export const monthTitle = (index: number): string => `${MONTH_NAMES[index % 12]} ${yearOf(index)}`;

// ---------------------------------------------------------------------------
// Type: thirty-one categories, folded into groups a chart can show
// ---------------------------------------------------------------------------

/** The order is the colour: a type keeps its colour whatever the period. */
export const TYPE_GROUPS = [
  { id: 'visits', label: 'Doctor & hospital visits' },
  { id: 'otc', label: 'Over-the-counter & supplies' },
  { id: 'rx', label: 'Prescriptions' },
  { id: 'dental', label: 'Dental' },
  { id: 'vision', label: 'Vision & hearing' },
  { id: 'travel', label: 'Travel' },
  { id: 'other', label: 'Other' },
  { id: 'unsorted', label: 'Not sorted' },
] as const;

export type TypeId = (typeof TYPE_GROUPS)[number]['id'];

/** Every category id in labels.ts is listed; the test fails if one is added and not placed. */
export const TYPE_OF_CATEGORY: Record<string, TypeId> = {
  medical_services: 'visits',
  hospital: 'visits',
  lab_imaging: 'visits',
  mental_health: 'visits',
  chiropractic: 'visits',
  physical_therapy: 'visits',
  fertility: 'visits',
  long_term_care_services: 'visits',
  otc_drugs: 'otc',
  menstrual_care: 'otc',
  medical_equipment: 'otc',
  first_aid: 'otc',
  supplements: 'otc',
  prescription_drugs: 'rx',
  dental: 'dental',
  vision: 'vision',
  hearing: 'vision',
  transportation: 'travel',
  lodging_medical: 'travel',
  massage: 'other',
  weight_loss: 'other',
  exercise_equipment: 'other',
  special_foods: 'other',
  home_modification: 'other',
  cosmetic: 'other',
  teeth_whitening: 'other',
  general_wellness: 'other',
  gym_membership: 'other',
  insurance_premium: 'other',
  other_non_qualified: 'other',
  uncategorized: 'unsorted',
};

export function typeOf(category: string | null | undefined): TypeId {
  if (!category) return 'unsorted';
  return TYPE_OF_CATEGORY[category] ?? 'other';
}

// ---------------------------------------------------------------------------
// Place: the merchant, with its spellings folded together
// ---------------------------------------------------------------------------

/** Places that are not a merchant's name as read from a receipt. */
const FIXED_PLACES: Record<string, string> = { amazon: 'Amazon', mileage: 'Mileage', '': 'No name' };

/**
 * "MDLIVE Medical Group PA / Dr. A. Person, MD" is a visit to MDLIVE. The
 * doctor is who was seen, not where, and a telehealth service that names a
 * different doctor on every receipt would otherwise be a different place each
 * time.
 */
function withoutPractitioner(name: string): string {
  return name
    .replace(/\s+\/\s+.*$/, '') // "Clinic / Dr. Name, MD"
    .replace(/\s*[,–—-]\s*(dr\.?|doctor)\s.*$/i, ''); // "Clinic - Dr. Name", "Clinic, Dr. Name"
}

/**
 * "CVS Pharmacy #4471", "CVS/pharmacy" and "cvs pharmacy 04471" are one place.
 * Receipts name the same shop several ways, and a chart with three slivers for
 * one pharmacy answers nothing.
 */
function tidy(name: string): string {
  return withoutPractitioner(name)
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/#\s*\d+/g, ' ') // a store number: "#4471"
    .replace(/\b(store|location)\s*\d+\b/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\b(inc|llc|ltd|corp|co)\b/g, ' ')
    .replace(/\s+\d{3,}\s*$/, ' ') // a trailing store or order number
    .replace(/\s+/g, ' ')
    .trim();
}

/** A record's place from its own text alone. `placesOf` then folds short and long forms together. */
export function placeKey(e: Pick<Expense, 'source' | 'provider_name'>): string {
  if (e.source === 'mileage') return 'mileage';
  if (e.source === 'amazon_import') return 'amazon';
  const key = tidy(e.provider_name ?? '');
  // An Amazon receipt added by hand belongs with the imported orders.
  return /^amazon\b/.test(key) ? 'amazon' : key;
}

export interface Places {
  /** The place a record belongs to, after folding. */
  keyOf: (e: Pick<Expense, 'source' | 'provider_name'>) => string;
  /** What to call each place. */
  names: Map<string, string>;
}

/**
 * The places in a set of records.
 *
 * One more fold needs the whole set: a merchant written short on one receipt
 * and long on another ("MDLIVE", "MDLIVE online", "MDLIVE Medical Group PA")
 * is one place, called by its short name. So a place whose words begin with
 * another place's whole name joins it. Three letters is the floor ("CVS"), so
 * a stray "Dr" cannot swallow every doctor.
 *
 * A place is called by its most common spelling, without the store number.
 */
export function placesOf(expenses: Pick<Expense, 'source' | 'provider_name'>[]): Places {
  const own = new Set(expenses.map(placeKey));
  const bases = [...own].filter((k) => k.length >= 3 && !Object.hasOwn(FIXED_PLACES, k)).sort((a, b) => a.length - b.length);
  const folded = new Map<string, string>();
  for (const key of own) folded.set(key, bases.find((base) => key.startsWith(`${base} `)) ?? key);
  const keyOf: Places['keyOf'] = (e) => {
    const key = placeKey(e);
    return folded.get(key) ?? key;
  };

  const votes = new Map<string, Map<string, number>>();
  for (const e of expenses) {
    const key = keyOf(e);
    // Only the spellings of the short form itself get a say in the name.
    if (Object.hasOwn(FIXED_PLACES, key) || placeKey(e) !== key) continue;
    const written = withoutPractitioner(e.provider_name ?? '')
      .replace(/\s*#\s*\d+/g, '')
      .replace(/\s+\d{3,}\s*$/, '')
      .replace(/[\s.,;:]+$/, '')
      .replace(/\s+/g, ' ')
      .trim();
    const tally = votes.get(key) ?? new Map<string, number>();
    tally.set(written, (tally.get(written) ?? 0) + 1);
    votes.set(key, tally);
  }
  const names = new Map(Object.entries(FIXED_PLACES));
  for (const [key, tally] of votes) {
    const [winner] = [...tally].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
    names.set(key, winner![0]);
  }
  return { keyOf, names };
}

// ---------------------------------------------------------------------------
// What counts
// ---------------------------------------------------------------------------

/** Filed: a person pressed "Looks right". Nothing else is a fact yet. */
export const isFiled = (e: Pick<Expense, 'status'>): boolean => e.status === 'verified' || e.status === 'reimbursed';

/** One filed expense, reduced to what the charts need. */
export interface Entry {
  id: string;
  /** The month of the care or purchase. */
  month: number;
  cents: number;
  type: TypeId;
  place: string;
  person: string;
  /** The month the HSA paid it back, if it has. */
  paidBack: number | null;
}

export interface Prepared {
  entries: Entry[];
  /** Records still in review. They are not counted, and the page says so. */
  waiting: number;
  places: Map<string, string>;
}

export function prepare(expenses: Expense[], reimbursements: Reimbursement[] = []): Prepared {
  // Unmatching a withdrawal leaves its reimbursement record behind, so the
  // newest one that names an expense is the one in force.
  const paidOn = new Map<string, string>();
  for (const r of [...reimbursements].sort((a, b) => a.created_at.localeCompare(b.created_at))) {
    for (const id of r.expense_ids) paidOn.set(id, r.distribution_date);
  }

  const filed = expenses.filter(isFiled);
  const places = placesOf(filed);
  const entries: Entry[] = [];
  for (const e of filed) {
    const month = monthOf(e.date_of_service);
    if (month === null || e.amount_claimed_cents === null) continue;
    entries.push({
      id: e.expense_id,
      month,
      cents: e.amount_claimed_cents,
      type: typeOf(e.category),
      place: places.keyOf(e),
      person: e.patient_id ?? '',
      paidBack:
        e.status === 'reimbursed' ? (monthOf(paidOn.get(e.expense_id)) ?? monthOf(e.updated_at) ?? month) : null,
    });
  }
  return { entries, waiting: expenses.filter((e) => e.status === 'draft').length, places: places.names };
}

// ---------------------------------------------------------------------------
// Periods and the slices they are drawn in
// ---------------------------------------------------------------------------

export type Unit = 'month' | 'quarter' | 'year';

export interface Bucket {
  from: number;
  to: number;
  /** Under the bar: "Aug", "Q3", "2026". */
  label: string;
  /** A second line, where the year turns. */
  year: string;
  /** In full: "Aug 2026". */
  title: string;
  /** Still in progress, so its bar is not yet its final height. */
  partial: boolean;
}

export interface Period {
  id: string;
  from: number;
  to: number;
  unit: Unit;
  buckets: Bucket[];
  months: number;
}

/** `last12`, then each year that has a filed expense, newest first, then `all`. */
export function periodIds(entries: Entry[], now: number): string[] {
  const years = [...new Set(entries.filter((e) => e.month <= now).map((e) => yearOf(e.month)))].sort((a, b) => b - a);
  return ['last12', ...years.map(String), 'all'];
}

export function periodOf(id: string, entries: Entry[], now: number): Period {
  const first = entries.reduce((min, e) => Math.min(min, e.month), now);
  let from = now - 11;
  let to = now;
  if (id === 'all') {
    from = first;
  } else if (/^\d{4}$/.test(id)) {
    const year = Number(id);
    // A year starts where the records start and stops at today.
    from = yearOf(first) === year ? first : monthIndex(year, 1);
    to = yearOf(now) === year ? now : monthIndex(year, 12);
  } else {
    id = 'last12';
  }
  const months = to - from + 1;
  // Months while they fit; then quarters; then, after five years, years.
  const unit: Unit = months <= 18 ? 'month' : months <= 60 ? 'quarter' : 'year';
  const buckets: Bucket[] = [];
  if (unit === 'month') {
    for (let i = from; i <= to; i++) {
      buckets.push({
        from: i,
        to: i,
        label: MONTH_NAMES[i % 12]!,
        year: i % 12 === 0 || i === from ? String(yearOf(i)) : '',
        title: monthTitle(i),
        partial: i === now,
      });
    }
  } else if (unit === 'quarter') {
    const start = Math.floor(from / 3);
    for (let q = start; q <= Math.floor(to / 3); q++) {
      const n = (q % 4) + 1;
      const year = Math.floor(q / 4);
      buckets.push({
        from: q * 3,
        to: q * 3 + 2,
        label: `Q${n}`,
        year: n === 1 || q === start ? String(year) : '',
        title: `Q${n} ${year}`,
        partial: q === Math.floor(now / 3),
      });
    }
  } else {
    for (let year = yearOf(from); year <= yearOf(to); year++) {
      buckets.push({
        from: monthIndex(year, 1),
        to: monthIndex(year, 12),
        label: String(year),
        year: '',
        title: String(year),
        partial: year === yearOf(now),
      });
    }
  }
  return { id, from, to, unit, buckets, months };
}

/** Which slice a month falls in, or -1. */
export function bucketOf(period: Period, month: number): number {
  if (month < period.from || month > period.to) return -1;
  const first = period.buckets[0]!.from;
  if (period.unit === 'month') return month - first;
  if (period.unit === 'quarter') return Math.floor(month / 3) - Math.floor(first / 3);
  return yearOf(month) - yearOf(first);
}

// ---------------------------------------------------------------------------
// Splitting the bars
// ---------------------------------------------------------------------------

export type Measure = 'amount' | 'count';
export type SplitBy = 'total' | 'type' | 'place' | 'person';

/** Colour slots: six hues, then "everything else", then "not known". */
export const OTHER_SLOT = 6;
export const NONE_SLOT = 7;
/** Places beyond this many are grouped; a stack of twelve colours reads as noise. */
export const TOP_PLACES = 5;

export interface Series {
  /** A type id, a place key or a person id. Null for the grouped tail. */
  key: string | null;
  name: string;
  note: string | null;
  slot: number;
  cents: number;
  count: number;
}

export interface Split {
  by: SplitBy;
  list: Series[];
  indexOf: (e: Entry) => number;
}

export function seriesOf(
  by: SplitBy,
  rows: Entry[],
  measure: Measure,
  names: { people: Pick<CoveredPerson, 'person_id' | 'display_name'>[]; places: Map<string, string> },
): Split {
  const cents = rows.reduce((t, e) => t + e.cents, 0);
  if (by === 'total') {
    return { by, list: [{ key: null, name: 'All expenses', note: null, slot: 0, cents, count: rows.length }], indexOf: () => 0 };
  }

  const sums = new Map<string, { cents: number; count: number }>();
  for (const e of rows) {
    const sum = sums.get(e[by]) ?? { cents: 0, count: 0 };
    sum.cents += e.cents;
    sum.count += 1;
    sums.set(e[by], sum);
  }

  const describe = (key: string, rank: number): { name: string; slot: number } => {
    if (by === 'type') {
      const slot = TYPE_GROUPS.findIndex((g) => g.id === key);
      return { name: TYPE_GROUPS[slot]?.label ?? key, slot: slot === -1 ? OTHER_SLOT : slot };
    }
    if (by === 'person') {
      const slot = names.people.findIndex((p) => p.person_id === key);
      if (slot === -1) return { name: 'Someone else', slot: NONE_SLOT };
      return { name: names.people[slot]!.display_name, slot: slot < OTHER_SLOT ? slot : OTHER_SLOT };
    }
    return { name: names.places.get(key) ?? key, slot: rank };
  };

  const size = (key: string): number => (measure === 'amount' ? sums.get(key)!.cents : sums.get(key)!.count);
  const named = [...sums.keys()].map((key) => ({ key, name: describe(key, 0).name }));
  // Biggest first. The name settles ties, so the order does not flicker.
  named.sort((a, b) => size(b.key) - size(a.key) || a.name.localeCompare(b.name));

  const keep = by === 'place' ? named.slice(0, TOP_PLACES) : named;
  const rest = by === 'place' ? named.slice(TOP_PLACES) : [];
  const list: Series[] = keep.map(({ key }, rank) => ({ key, note: null, ...describe(key, rank), ...sums.get(key)! }));
  if (rest.length > 0) {
    list.push({
      key: null,
      name: 'Everything else',
      note: `${rest.length} place${rest.length === 1 ? '' : 's'}`,
      slot: OTHER_SLOT,
      cents: rest.reduce((t, r) => t + sums.get(r.key)!.cents, 0),
      count: rest.reduce((t, r) => t + sums.get(r.key)!.count, 0),
    });
  }
  const where = new Map(keep.map(({ key }, index) => [key, index]));
  return { by, list, indexOf: (e) => where.get(e[by]) ?? list.length - 1 };
}

/** One slice of time, one number per series. */
export interface Stack {
  cents: number[];
  count: number[];
}

export function stacks(period: Period, split: Split, rows: Entry[]): Stack[] {
  const out: Stack[] = period.buckets.map(() => ({ cents: split.list.map(() => 0), count: split.list.map(() => 0) }));
  for (const e of rows) {
    const slice = out[bucketOf(period, e.month)];
    if (!slice) continue;
    const s = split.indexOf(e);
    slice.cents[s]! += e.cents;
    slice.count[s]! += 1;
  }
  return out;
}

/**
 * Ready to claim as it stood at the end of each slice: filed, and not yet paid
 * back by then. It is cumulative, so it counts everything before the period
 * too, and it steps down in the month a withdrawal was matched.
 */
export function balance(period: Period, entries: Entry[]): number[] {
  return period.buckets.map((b) => {
    const at = Math.min(b.to, period.to);
    let cents = 0;
    for (const e of entries) {
      if (e.month <= at && !(e.paidBack !== null && e.paidBack <= at)) cents += e.cents;
    }
    return cents;
  });
}

/** A round axis: the top value and the gap between gridlines. */
export function niceAxis(max: number, whole: boolean): { max: number; step: number } {
  if (max <= 0) return { max: whole ? 4 : 1, step: whole ? 1 : 0.25 };
  const raw = max / 4;
  const magnitude = Math.pow(10, Math.floor(Math.log10(raw)));
  const n = raw / magnitude;
  let step = (n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10) * magnitude;
  if (whole) step = Math.max(1, Math.round(step));
  return { max: Math.ceil(max / step - 1e-9) * step, step };
}

// ---------------------------------------------------------------------------
// From a bar or a row to the records behind it
// ---------------------------------------------------------------------------

/** What a click on Trends asks the Expenses page to show. */
export interface Scope {
  from: number | null;
  to: number | null;
  type: string | null;
  place: string | null;
  person: string | null;
}

const SCOPE_KEYS = ['from', 'to', 'type', 'place', 'person'] as const;

export function scopeQuery(scope: Partial<Scope>): string {
  const params = new URLSearchParams();
  if (scope.from != null) params.set('from', monthKey(scope.from));
  if (scope.to != null) params.set('to', monthKey(scope.to));
  for (const key of ['type', 'place', 'person'] as const) {
    if (scope[key] != null) params.set(key, scope[key]!);
  }
  return params.toString();
}

/** Null when the address carries no scope, which is the Expenses page as it always was. */
export function scopeFrom(params: URLSearchParams): Scope | null {
  if (!SCOPE_KEYS.some((key) => params.has(key))) return null;
  return {
    from: monthOf(params.get('from')),
    to: monthOf(params.get('to')),
    type: params.get('type'),
    place: params.get('place'),
    person: params.get('person'),
  };
}

/**
 * The same test the charts apply, so the list adds up to the bar that led to
 * it. `places` must come from the same filed records the chart was drawn from.
 */
export function inScope(e: Expense, scope: Scope, places: Places): boolean {
  if (!isFiled(e)) return false;
  const month = monthOf(e.date_of_service);
  if (month === null || e.amount_claimed_cents === null) return false;
  if (scope.from !== null && month < scope.from) return false;
  if (scope.to !== null && month > scope.to) return false;
  if (scope.type !== null && typeOf(e.category) !== scope.type) return false;
  if (scope.place !== null && places.keyOf(e) !== scope.place) return false;
  if (scope.person !== null && (e.patient_id ?? '') !== scope.person) return false;
  return true;
}

/** "Aug 2026", "2025", or "Nov 2025 – Oct 2026". */
export function rangeLabel(from: number | null, to: number | null): string | null {
  if (from === null && to === null) return null;
  if (from === null) return `Through ${monthTitle(to!)}`;
  if (to === null) return `Since ${monthTitle(from)}`;
  if (from === to) return monthTitle(from);
  if (yearOf(from) === yearOf(to) && from % 12 === 0 && to % 12 === 11) return String(yearOf(from));
  return `${monthTitle(from)} – ${monthTitle(to)}`;
}

export function scopeLabel(
  scope: Scope,
  names: { people: Pick<CoveredPerson, 'person_id' | 'display_name'>[]; places: Map<string, string> },
): string {
  return [
    rangeLabel(scope.from, scope.to),
    scope.type !== null ? (TYPE_GROUPS.find((g) => g.id === scope.type)?.label ?? scope.type) : null,
    scope.place !== null ? (names.places.get(scope.place) ?? scope.place) : null,
    scope.person !== null
      ? (names.people.find((p) => p.person_id === scope.person)?.display_name ?? 'Someone else')
      : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

// ---------------------------------------------------------------------------
// The HSA itself: what it holds, and how much of that is growth (TR-70)
// ---------------------------------------------------------------------------

/** After this long the balance is worth copying in again. About three months. */
export const STALE_AFTER_DAYS = 90;

export function latestBalance(list: HsaBalance[] | null | undefined): HsaBalance | null {
  return [...(list ?? [])].sort((a, b) => b.as_of.localeCompare(a.as_of))[0] ?? null;
}

/** Whole days from a `YYYY-MM-DD` to the viewer's today. Counted on calendar days, so no clock hour can tip it. */
export function daysSince(day: string, today: Date): number {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  return Math.round((Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()) - Date.UTC(y, m - 1, d)) / 86_400_000);
}

export const isStale = (b: HsaBalance, today: Date): boolean => daysSince(b.as_of, today) > STALE_AFTER_DAYS;

export interface BalanceSplit {
  balance: number;
  /** What went in and is still there: contributed less withdrawn. */
  kept: number;
  /** The rest of the balance. Negative when the investments are down. */
  growth: number;
  /** Growth as a share of what went in: 0.042 is 4.2%. Null when nothing went in. */
  growthRate: number | null;
  /** How wide each part of the balance bar is, 0–1. They add up to 1. */
  keptShare: number;
  growthShare: number;
}

export function splitBalance(b: Pick<HsaBalance, 'balance_cents' | 'contributed_cents' | 'withdrawn_cents'>): BalanceSplit {
  const kept = b.contributed_cents - b.withdrawn_cents;
  const growth = b.balance_cents - kept;
  const shown = Math.max(0, kept) + Math.max(0, growth);
  return {
    balance: b.balance_cents,
    kept,
    growth,
    growthRate: b.contributed_cents > 0 ? growth / b.contributed_cents : null,
    keptShare: shown > 0 ? Math.max(0, kept) / shown : 0,
    growthShare: shown > 0 ? Math.max(0, growth) / shown : 0,
  };
}

/** A reading as a point on the time line, for the chart on the HSA card. */
export interface BalancePoint {
  as_of: string;
  /** Days since 1970-01-01, on the calendar: the x of the point. */
  day: number;
  balance: number;
  /** Contributed less withdrawn, never drawn above the balance. */
  kept: number;
  growth: number;
}

export function dayNumber(iso: string): number {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  return Date.UTC(y, m - 1, d) / 86_400_000;
}

/** Oldest first. Readings come at whatever dates they were copied in, so x is time, not a slot. */
export function balanceSeries(list: HsaBalance[] | null | undefined): BalancePoint[] {
  return [...(list ?? [])]
    .sort((a, b) => a.as_of.localeCompare(b.as_of))
    .map((b) => {
      const s = splitBalance(b);
      return { as_of: b.as_of, day: dayNumber(b.as_of), balance: s.balance, kept: Math.min(Math.max(0, s.kept), s.balance), growth: s.growth };
    });
}

/**
 * Labels along a time line: the first of a month, a quarter or a year,
 * whichever gives no more than about seven. The year is written where it turns.
 */
export function timeTicks(from: number, to: number): { day: number; label: string; year: string }[] {
  const start = new Date(from * 86_400_000);
  const months = (to - from) / 30.44;
  const step = months <= 7 ? 1 : months <= 14 ? 2 : months <= 21 ? 3 : months <= 42 ? 6 : 12;
  const y = start.getUTCFullYear();
  // The first month that begins inside the span, then on to the step: quarters
  // fall on Jan, Apr, Jul and Oct, and years on January.
  let m = start.getUTCMonth() + (start.getUTCDate() === 1 ? 0 : 1);
  m = Math.ceil(m / step) * step;
  const out: { day: number; label: string; year: string }[] = [];
  for (; ; m += step) {
    const day = Date.UTC(y + Math.floor(m / 12), m % 12, 1) / 86_400_000;
    if (day > to) break;
    if (day < from) continue;
    const month = m % 12;
    const year = y + Math.floor(m / 12);
    out.push({
      day,
      label: step === 12 ? String(year) : MONTH_NAMES[month]!,
      year: step !== 12 && (month === 0 || out.length === 0) ? String(year) : '',
    });
  }
  return out;
}
